import {
  Lead,
  GuideSection,
  P,
  UL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/**
 * Body for "What is an agentic SOC?" guide. Educational-first; LogPulse's
 * propose-approve approach is described at the end.
 */
export function AgenticSocGuideBody() {
  return (
    <>
      <Lead>
        An agentic SOC uses AI agents that can plan and run an investigation,
        triaging alerts, gathering evidence, and reasoning to a verdict, rather
        than just suggesting next steps. The promise is machine-speed handling of
        alert volume; the risk is handing over decisions you cannot see or reverse.
        This guide explains what an agentic SOC is, how it differs from older
        automation, and why governance is the part that actually matters.
      </Lead>

      <GuideSection id="what-is-it" title="What is an agentic SOC?">
        <P>
          An <Term>agentic SOC</Term> is a security-operations model in which one or
          more AI agents can perceive context, plan a multi-step investigation, make
          decisions, and take or recommend actions toward a goal, under
          human-defined constraints. Instead of only answering a prompt or scoring an
          alert, an agent can decide what to look at next based on what it just
          found, correlate across data sources, and deliver a reasoned verdict.
        </P>
      </GuideSection>

      <GuideSection id="vs-soar" title="How is it different from SOAR and automation?">
        <P>
          Earlier SOC automation, such as SOAR playbooks, static correlation rules
          and ML-based alert scoring, follows a <Term>fixed decision tree</Term> a human
          wrote in advance. Agentic systems plan <Term>dynamically</Term>: they
          adjust the investigation path based on findings and reason across multiple
          sources in sequence.
        </P>
        <GuideTable
          head={['', 'SOAR / classic automation', 'Agentic SOC']}
          rows={[
            ['Logic', 'Pre-written playbook / decision tree', 'Dynamic, plans based on findings'],
            ['Scope', 'Each step scripted in advance', 'End-to-end investigation toward a goal'],
            ['Adaptability', 'Breaks on the unexpected path', 'Adjusts the path as evidence emerges'],
            ['Output', 'Action when conditions match', 'Reasoned verdict with evidence + citations'],
          ]}
        />
      </GuideSection>

      <GuideSection id="capabilities" title="Core capabilities">
        <UL>
          <LI><Term>Automated triage</Term>: evaluate incoming alerts, enrich with context, and judge likely true/false positive before an analyst sees them.</LI>
          <LI><Term>Investigation</Term>: gather evidence, decode or correlate signals across tools, and build a timeline.</LI>
          <LI><Term>Verdict with explanation</Term>: deliver a conclusion with traceable evidence and citations the analyst can verify.</LI>
          <LI><Term>Detection engineering & hunting</Term>: propose new detections and pursue hypotheses across historical data.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="human-in-the-loop" title="Human-in-the-loop: the part that matters">
        <P>
          The 2026 consensus is augmentation, not replacement. The winning model is
          often described as <Term>human-on-the-loop</Term>: AI handles alert volume
          at machine speed, while humans own judgment, business context, novel
          threats, and any consequential action. Analysts shift from triaging alerts
          to <Term>supervising outcomes</Term>, validating agent-led investigations
          and stepping in on ambiguous cases.
        </P>
        <Callout title="Autonomy is a spectrum: choose deliberately">
          <p>
            The dangerous default is an agent that takes irreversible action
            (disabling accounts, blocking traffic, changing config) on its own. For
            most teams, and for regulated ones especially, the right design lets
            agents investigate and <Term>draft</Term> changes freely, but gates any
            change to your environment behind human approval. Transparency
            (citations, an auditable trail) is what makes that supervision possible.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="why-it-matters" title="Why teams are adopting it">
        <UL>
          <LI><Term>Alert fatigue</Term>: agents absorb the repetitive triage that burns analysts out.</LI>
          <LI><Term>Talent shortage</Term>: they extend a small team&apos;s effective capacity.</LI>
          <LI><Term>MTTR</Term>: machine-speed investigation shortens time to a verdict.</LI>
          <LI><Term>Analyst growth</Term>: humans move to hunting, complex cases, and strategic work.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse approaches the agentic SOC">
        <P>
          LogPulse is built around <Term>an agentic SOC with a human in the loop</Term>.
          Notables raised by{' '}
          <GuideLink href="/guides/what-is-risk-based-alerting">risk-based alerting</GuideLink>{' '}
          are AI auto-investigated and benign ones auto-close, so analysts see a
          handful of high-confidence cases. AI agents, over the{' '}
          <GuideLink href="/guides/mcp-for-security">MCP server</GuideLink>, can
          draft detections, alert rules and response playbooks, but every change is{' '}
          <Term>created disabled until a human approves it</Term>; nothing is applied
          to your environment directly, and destructive actions need owner approval.
        </P>
        <P>
          That propose-and-approve gate is the difference between speed with control
          and unattended automation. See{' '}
          <GuideLink href="/agentic-soc">Agentic SOC</GuideLink> and{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink> for how it
          fits together.
        </P>
      </GuideSection>
    </>
  );
}
